import {
  Alert, AlertIcon, Badge, Box, Button, Container, Divider, Flex, HStack,
  Heading, SimpleGrid, Skeleton, Stack, Text, useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BILLING_ENABLED } from '../featureFlags';
import {
  CreditPack,
  SubPlan,
  useCreateBuyCreditsSessionMutation,
  useGetBillingPortalUrlMutation,
  useGetCreditPacksQuery,
  useGetCreditsBalanceQuery,
  useGetSubPlansQuery,
  useInitCheckoutMutation,
} from '../redux/services/mixgenie';

export default function CreditsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  const { data: creditsData, isLoading: balanceLoading, refetch: refetchBalance } = useGetCreditsBalanceQuery();
  const { data: packs, isLoading: packsLoading } = useGetCreditPacksQuery(undefined, { skip: !BILLING_ENABLED });
  const { data: plans, isLoading: plansLoading } = useGetSubPlansQuery(undefined, { skip: !BILLING_ENABLED });
  const [buyPack, { isLoading: buyingPack }] = useCreateBuyCreditsSessionMutation();
  const [initCheckout, { isLoading: startingSub }] = useInitCheckoutMutation();
  const [openPortal, { isLoading: openingPortal }] = useGetBillingPortalUrlMutation();

  const balance = creditsData?.data || { creditsRemaining: 0, plan: 'starter', currentPlan: 0, purchased: 0, status: null };
  const hasActiveSub = balance.status === 'active';

  // Toast + refetch after coming back from Stripe.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      toast({ title: t('credits.checkoutSuccess', 'Payment received — your credits are updating.'), status: 'success', duration: 4000, isClosable: true });
      refetchBalance();
    } else if (checkout === 'cancelled') {
      toast({ title: t('credits.checkoutCancelled', 'Checkout cancelled.'), status: 'info', duration: 3000, isClosable: true });
    }
    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, toast, refetchBalance, t]);

  const returnUrl = `${window.location.origin}/credits?checkout=success`;
  const cancelUrl = `${window.location.origin}/credits?checkout=cancelled`;

  const onBuyPack = async (pack: CreditPack) => {
    const priceId = pack.metadata?.subscription?.priceID;
    if (!priceId) {
      toast({ title: 'This pack is missing a Stripe price — contact support.', status: 'error' });
      return;
    }
    try {
      const res: any = await buyPack({ priceId, creditAmount: pack.amount, quantity: 1, returnUrl }).unwrap();
      const url = res?.data?.url || res?.url;
      if (url) window.location.href = url;
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Could not start checkout', status: 'error' });
    }
  };

  const onSubscribe = async (plan: SubPlan) => {
    try {
      const res: any = await initCheckout({
        planID: plan._id,
        billingCycle,
        successUrl: returnUrl,
        cancelUrl,
      }).unwrap();
      const url = res?.data?.url || res?.data?.checkoutUrl || res?.url;
      if (url) window.location.href = url;
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Could not start checkout', status: 'error' });
    }
  };

  const onManageBilling = async () => {
    try {
      const res: any = await openPortal().unwrap();
      const url = res?.data?.url || res?.url;
      if (url) window.location.href = url;
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Could not open billing portal', status: 'error' });
    }
  };

  return (
      <Container maxW="1100px" py={8}>
        <Heading size="lg" color="whiteAlpha.900" mb={6}>
          {t('credits.title', 'Credits')}
        </Heading>

        {/* --- Balance card --- */}
        <Box bg="night.800" border="1px solid" borderColor="whiteAlpha.200" borderRadius="12px" p={6} mb={8}>
          {balanceLoading ? (
            <Skeleton height="88px" />
          ) : (
            <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
              <Stack spacing={1}>
                <Text fontSize="sm" color="whiteAlpha.700">
                  {t('credits.balanceLabel', 'Credits remaining')}
                </Text>
                <HStack spacing={3} align="baseline">
                  <Text fontSize="4xl" fontWeight={800} color={balance.creditsRemaining > 0 ? 'accent.400' : 'red.300'}>
                    {balance.creditsRemaining}
                  </Text>
                  {balance.plan && (
                    <Badge colorScheme="pink" textTransform="none">{balance.plan}</Badge>
                  )}
                  {balance.isTrial && <Badge colorScheme="yellow">trial</Badge>}
                </HStack>
                <Text fontSize="xs" color="whiteAlpha.600">
                  {t('credits.breakdown', 'Plan: {{plan}} · Purchased: {{purchased}}', { plan: balance.currentPlan, purchased: balance.purchased })}
                </Text>
              </Stack>
              {BILLING_ENABLED && hasActiveSub && (
                <Button variant="outline" colorScheme="whiteAlpha" onClick={onManageBilling} isLoading={openingPortal}>
                  {t('credits.manageBilling', 'Manage billing')}
                </Button>
              )}
            </Flex>
          )}
        </Box>

        {balance.creditsRemaining === 0 && BILLING_ENABLED && (
          <Alert status="warning" borderRadius="10px" mb={8}>
            <AlertIcon />
            {t('credits.outOfCredits', "You're out of credits. Grab a pack or subscribe below.")}
          </Alert>
        )}

        {!BILLING_ENABLED && (
          <Alert status="info" borderRadius="10px" mb={8} bg="whiteAlpha.100" color="whiteAlpha.900" borderWidth="1px" borderColor="whiteAlpha.200">
            <AlertIcon color="accent.400" />
            {t('credits.comingSoon', 'Paid plans and credit packs are coming soon. Enjoy your free credits!')}
          </Alert>
        )}

        {/* --- Credit packs --- */}
        {BILLING_ENABLED && (
          <Box mb={10}>
            <Heading size="md" color="whiteAlpha.900" mb={4}>
              {t('credits.packsTitle', 'Credit packs')}
            </Heading>
            <Text color="whiteAlpha.700" fontSize="sm" mb={4}>
              {t('credits.packsHelp', 'One-time top-ups. Credits never expire.')}
            </Text>
            {packsLoading ? (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {[1, 2, 3].map((i) => (<Skeleton key={i} height="180px" borderRadius="12px" />))}
              </SimpleGrid>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {(packs || []).map((pack) => (
                  <PackCard key={pack._id} pack={pack} onBuy={onBuyPack} disabled={buyingPack} />
                ))}
              </SimpleGrid>
            )}
          </Box>
        )}

        {/* --- Subscription plans --- */}
        {BILLING_ENABLED && (
          <Box>
            <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
              <Heading size="md" color="whiteAlpha.900">
                {t('credits.plansTitle', 'Monthly plans')}
              </Heading>
              <HStack spacing={0} bg="whiteAlpha.200" borderRadius="full" p={1}>
                <Button
                  size="sm"
                  variant={billingCycle === 'monthly' ? 'solid' : 'ghost'}
                  colorScheme={billingCycle === 'monthly' ? 'pink' : 'whiteAlpha'}
                  onClick={() => setBillingCycle('monthly')}
                  borderRadius="full"
                >
                  {t('credits.monthly', 'Monthly')}
                </Button>
                <Button
                  size="sm"
                  variant={billingCycle === 'annual' ? 'solid' : 'ghost'}
                  colorScheme={billingCycle === 'annual' ? 'pink' : 'whiteAlpha'}
                  onClick={() => setBillingCycle('annual')}
                  borderRadius="full"
                >
                  {t('credits.annual', 'Annual')}
                </Button>
              </HStack>
            </Flex>
            {plansLoading ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {[1, 2].map((i) => (<Skeleton key={i} height="220px" borderRadius="12px" />))}
              </SimpleGrid>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {(plans || []).map((plan) => (
                  <PlanCard
                    key={plan._id}
                    plan={plan}
                    cycle={billingCycle}
                    onSubscribe={onSubscribe}
                    disabled={startingSub}
                    isCurrent={hasActiveSub && plan._id === balance.planID}
                  />
                ))}
              </SimpleGrid>
            )}
            <Divider borderColor="whiteAlpha.200" my={8} />
            <Text fontSize="xs" color="whiteAlpha.600">
              {t('credits.disclaimer', 'Subscriptions renew automatically. You can cancel anytime from Manage billing.')}
            </Text>
          </Box>
        )}
      </Container>
  );
}

function PackCard({ pack, onBuy, disabled }: { pack: CreditPack; onBuy: (p: CreditPack) => void; disabled: boolean }) {
  return (
    <Box bg="night.800" border="1px solid" borderColor="whiteAlpha.200" borderRadius="12px" p={5}>
      <Stack spacing={3}>
        <Text fontSize="sm" color="whiteAlpha.700">{pack.name}</Text>
        <HStack spacing={2} align="baseline">
          <Text fontSize="3xl" fontWeight={800} color="whiteAlpha.900">{pack.amount}</Text>
          <Text fontSize="sm" color="whiteAlpha.700">credits</Text>
        </HStack>
        <Text fontSize="xl" color="whiteAlpha.900">€{pack.cost.toFixed(2)}</Text>
        <Button colorScheme="pink" onClick={() => onBuy(pack)} isDisabled={disabled} data-cta="credits:buy-pack">
          Buy pack
        </Button>
      </Stack>
    </Box>
  );
}

function PlanCard({ plan, cycle, onSubscribe, disabled, isCurrent }: {
  plan: SubPlan;
  cycle: 'monthly' | 'annual';
  onSubscribe: (p: SubPlan) => void;
  disabled: boolean;
  isCurrent: boolean;
}) {
  const bucket = plan.metadata?.subscription?.[cycle];
  const price = bucket?.cost;
  return (
    <Box bg="night.800" border="1px solid" borderColor={isCurrent ? 'accent.400' : 'whiteAlpha.200'} borderRadius="12px" p={5}>
      <Stack spacing={3}>
        <Flex justify="space-between" align="start">
          <Stack spacing={0}>
            <Text fontSize="sm" color="whiteAlpha.700">{plan.name}</Text>
            <Text fontSize="2xl" fontWeight={800} color="whiteAlpha.900">
              {plan.nTokens} credits/mo
            </Text>
          </Stack>
          {isCurrent && <Badge colorScheme="green">current</Badge>}
        </Flex>
        {price != null && (
          <Text fontSize="xl" color="whiteAlpha.900">
            €{Number(price).toFixed(2)}
            <Text as="span" fontSize="sm" color="whiteAlpha.700">/{cycle === 'monthly' ? 'mo' : 'yr'}</Text>
          </Text>
        )}
        <Button
          colorScheme="pink"
          onClick={() => onSubscribe(plan)}
          isDisabled={disabled || isCurrent}
          data-cta="credits:subscribe"
        >
          {isCurrent ? 'Your plan' : 'Subscribe'}
        </Button>
      </Stack>
    </Box>
  );
}
